import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Send, Stop } from "@mui/icons-material";
import {
  CHARS_LEFT_THRESHOLD,
  PLACEHOLDER_SX,
  VISUALLY_HIDDEN,
} from "src/_modules/AiSupportChat/constants";

/**
 * Touch-first devices (phones, tablets) have no Shift+Enter, so Enter adds
 * a line there and the Send button sends. The primary pointer decides, not
 * the user agent.
 */
const isTouchPrimary = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: coarse)").matches;

/**
 * The message input of an open conversation or a draft (plan §3.6): a 1–5
 * row field and one button that is Send, or Stop while an answer is in
 * flight.
 *
 * - Enter sends and Shift+Enter adds a line; on touch devices Enter adds a
 *   line. The Enter that confirms an IME composition never sends.
 * - The text is trimmed before it is counted and sent. Within
 *   `CHARS_LEFT_THRESHOLD` of `maxChars` a counter shows what is left; over
 *   it (or after the server refused the text as too long, 857) the field is
 *   invalid, the limit message replaces the counter and Send is blocked.
 * - `sendBlocked` (853 cooldown, 852) blocks Send only: the field stays
 *   editable, so the text the server handed back can be fixed meanwhile.
 *
 * Nothing here uses the `disabled` attribute: disabling the focused field or
 * button moves focus to <body>, where Esc no longer reaches the panel (D37,
 * C31). While an answer streams the field is read-only; a Send that can't
 * send is `aria-disabled`.
 *
 * @param {object} props
 * @param {boolean} props.busy an answer is in flight: the button is Stop
 * @param {boolean} [props.sendBlocked] sending is refused for now (853, 852)
 * @param {number} [props.maxChars] `maxMessageChars` from `/eligibility`
 * @param {boolean} [props.tooLongNotice] the server refused this text as too
 *   long (857); the first edit calls `onTooLongEdited`
 * @param {() => void} [props.onTooLongEdited]
 * @param {(text: string) => boolean} props.onSend true when sent (the field clears)
 * @param {() => void} props.onStop
 * @param {string | null} props.restoreText text the chat state hands back
 *   (a send rejected before streaming, a new draft's prefill)
 * @param {() => void} props.onRestoreTaken called once the field took it
 */
export default function Composer({
  busy,
  sendBlocked = false,
  maxChars,
  tooLongNotice = false,
  onTooLongEdited,
  onSend,
  onStop,
  restoreText,
  onRestoreTaken,
}) {
  const { t } = useTranslation("aiSupport");
  const theme = useTheme();
  const helperId = useId();
  const [text, setText] = useState(() => restoreText ?? "");

  useEffect(() => {
    if (restoreText === null || restoreText === undefined) return;
    setText(restoreText);
    onRestoreTaken();
  }, [restoreText, onRestoreTaken]);

  const limit = Number.isInteger(maxChars) && maxChars > 0 ? maxChars : null;
  // What `send` checks and the server counts (UTF-16 units, like Joi max())
  const length = text.trim().length;
  const tooLong = tooLongNotice || (limit !== null && length > limit);
  const charsLeft = limit === null ? null : limit - length;

  let helperText = null;
  if (tooLong) {
    helperText =
      limit === null
        ? t("composer.tooLongNoMax")
        : t("composer.tooLong", { max: limit });
  } else if (charsLeft !== null && charsLeft <= CHARS_LEFT_THRESHOLD) {
    helperText = t("composer.charsLeft", { count: charsLeft });
  }

  const canSend = !busy && !sendBlocked && !tooLong && length > 0;

  const submit = () => {
    if (canSend && onSend(text)) setText("");
  };

  const handleChange = (event) => {
    setText(event.target.value);
    if (tooLongNotice) onTooLongEdited?.();
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    // The Enter that confirms an IME composition isn't a send (Safari fires
    // it after compositionend, flagged only by keyCode 229)
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (isTouchPrimary()) return;
    event.preventDefault();
    submit();
  };

  const handleClick = (event) => {
    if (!busy) {
      submit();
      return;
    }
    // A double-click on Send: its second click lands on Stop
    if (event.detail > 1) return;
    onStop();
  };

  const label = busy ? t("composer.stop") : t("composer.send");

  return (
    <Box
      sx={{
        flexShrink: 0,
        borderTop: 1,
        borderColor: "divider",
        p: 1.5,
      }}
    >
      {/* The button stays level with the last line as the field grows */}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={1}
          maxRows={5}
          size="small"
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={t("composer.placeholder")}
          // Also sets aria-invalid on the textarea
          error={tooLong}
          inputProps={{
            "aria-label": t("composer.placeholder"),
            "aria-describedby": helperText ? helperId : undefined,
            readOnly: busy,
            // An empty field follows the UI direction (placeholder)
            dir: "auto",
          }}
          sx={{
            ...PLACEHOLDER_SX,
            "& .MuiInputBase-root": {
              bgcolor: busy ? "action.hover" : "background.paper",
            },
          }}
        />
        <Tooltip title={label}>
          <IconButton
            aria-label={label}
            aria-disabled={!busy && !canSend}
            color="primary"
            onClick={handleClick}
            sx={{
              flexShrink: 0,
              ...(!busy && !canSend && { color: "action.disabled" }),
            }}
          >
            {busy ? (
              <Stop />
            ) : (
              // Icons don't mirror: point the arrow along the reading direction
              <Send
                sx={{
                  transform: theme.direction === "rtl" ? "scaleX(-1)" : "none",
                }}
              />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      {helperText && (
        <Typography
          id={helperId}
          variant="caption"
          color={tooLong ? "error" : "text.secondary"}
          sx={{ display: "block", mt: 0.5, px: 0.5 }}
        >
          {helperText}
        </Typography>
      )}
      {/* Announced once when the text goes over the limit; the counter
          itself isn't live (it would be read on every keystroke) */}
      <Box aria-live="polite" sx={VISUALLY_HIDDEN}>
        {tooLong ? helperText : ""}
      </Box>
    </Box>
  );
}
